import { createClient } from '@supabase/supabase-js';

// Lendo as variáveis de ambiente do arquivo .env.local
console.log('URL do Supabase:', process.env.REACT_APP_SUPABASE_URL);
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://zquhwrqrsbgujyvwroed.supabase.co';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpxdWh3cnFyc2JndWp5dndyb2VkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDcyODEyNzYsImV4cCI6MjA2Mjg1NzI3Nn0.4NvCH-DdUsHKWLRW8O8m1NBqe_5X9___FcWOe_OAKtE';
// Para usar funções administrativas, seria necessário uma service_role key
// Mas para este projeto educacional, vamos usar apenas a anon key

export const supabase = createClient(supabaseUrl, supabaseKey);

// Serviços de autenticação
export const authService = {
  login: async (email: string, password: string) => {
    return await supabase.auth.signInWithPassword({ email, password });
  },
  
  logout: async () => {
    return await supabase.auth.signOut();
  },
  
  getCurrentUser: async () => {
    return await supabase.auth.getUser();
  },
  
  isAdmin: async () => {
    const { data, error } = await supabase
      .from('users')
      .select('tipo_usuario')
      .eq('id', (await supabase.auth.getUser()).data.user?.id)
      .single();
      
    if (error) return false;
    return data?.tipo_usuario === 'admin';
  }
};

// Serviços de usuário
export const userService = {
  getUsers: async () => {
    return await supabase
      .from('users')
      .select('*');
  },
  
  getUserById: async (id: string) => {
    return await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single();
  },
  
  createUser: async (userData: any) => {
    try {
      // Usando signUp em vez da API de administração
      const authResponse = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: {
            nome: userData.nome,
            tipo_usuario: userData.tipo_usuario || 'comum'
          }
        }
      });
      
      if (authResponse.error) {
        console.error("Erro na criação do usuário:", authResponse.error);
        return authResponse;
      }
      
      if (!authResponse.data.user) {
        return { error: new Error("Falha ao criar usuário") };
      }
      
      // Depois, adiciona os dados na tabela users
      const { error } = await supabase
        .from('users')
        .insert({
          id: authResponse.data.user.id,
          nome: userData.nome,
          email: userData.email,
          avatar_url: userData.avatar_url,
          tipo_usuario: userData.tipo_usuario || 'comum'
        });
        
      if (error) {
        console.error("Erro ao inserir na tabela users:", error);
        return { error };
      }
      
      return authResponse;
    } catch (err) {
      console.error("Erro inesperado ao criar usuário:", err);
      return { error: err instanceof Error ? err : new Error("Erro desconhecido") };
    }
  },
  
  updateUser: async (id: string, userData: any) => {
    return await supabase
      .from('users')
      .update(userData)
      .eq('id', id);
  },
  
  deleteUser: async (id: string) => {
    // Remove da tabela users
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', id);
      
    if (error) return { error };
    
    // Remove da autenticação - não podemos usar admin.deleteUser com anon key
    // Vamos apenas retornar sucesso
    return { data: { success: true } };
  }
};

// Serviços de materiais
export const materialService = {
  getMaterials: async () => {
    return await supabase
      .from('materiais')
      .select('*');
  },
  
  getMaterialById: async (id: string) => {
    return await supabase
      .from('materiais')
      .select('*')
      .eq('id', id)
      .single();
  },
  
  createMaterial: async (materialData: any) => {
    return await supabase
      .from('materiais')
      .insert(materialData);
  },
  
  updateMaterial: async (id: string, materialData: any) => {
    return await supabase
      .from('materiais')
      .update(materialData)
      .eq('id', id);
  },
  
  deleteMaterial: async (id: string) => {
    return await supabase
      .from('materiais')
      .delete()
      .eq('id', id);
  }
};

// Serviços de permissões
export const permissionService = {
  getUserPermissions: async (userId: string) => {
    return await supabase
      .from('permissoes_materiais')
      .select('material_id')
      .eq('user_id', userId);
  },
  
  addPermission: async (userId: string, materialId: string) => {
    return await supabase
      .from('permissoes_materiais')
      .insert({ user_id: userId, material_id: materialId });
  },
  
  removePermission: async (userId: string, materialId: string) => {
    return await supabase
      .from('permissoes_materiais')
      .delete()
      .eq('user_id', userId)
      .eq('material_id', materialId);
  },
  
  getUserMaterials: async (userId: string) => {
    return await supabase
      .from('permissoes_materiais')
      .select('materiais(*)')
      .eq('user_id', userId);
  }
};

// Serviço de upload de arquivos
export const storageService = {
  uploadAvatar: async (file: File, userId: string) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}-${Math.random()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;
      
      // Como você confirmou que o bucket avatars já existe, vamos tentar fazer upload diretamente
      console.log('Tentando fazer upload para o bucket avatars...');
      
      // Usando uma abordagem mais simples para o upload
      const { error, data } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true // Sobrescreve se já existir
        });
        
      if (error) {
        console.error('Erro no upload:', error);
        
        // Se falhar por problemas de permissão, retornamos um erro amigável
        if (error.message && (error.message.includes('security') || error.message.includes('policy'))) {
          return { 
            error: new Error('Erro de permissão: Contate o administrador para verificar as políticas de upload do Supabase')
          };
        }
        
        return { error };
      }
      
      // Obtém a URL pública
      const { data: publicUrlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
      
      if (!publicUrlData || !publicUrlData.publicUrl) {
        return { 
          error: new Error('Falha ao obter URL pública do avatar')
        };
      }
        
      return { data: { publicUrl: publicUrlData.publicUrl } };
    } catch (err) {
      console.error('Erro inesperado no upload:', err);
      return { error: err instanceof Error ? err : new Error('Erro desconhecido no upload') };
    }
  },
  
  uploadThumbnail: async (file: File, materialId: string) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${materialId}-${Math.random()}.${fileExt}`;
      const filePath = `thumbnails/${fileName}`;
      
      console.log('Tentando fazer upload para o bucket thumbnails...');
      
      // Abordagem simplificada para o upload
      const { error, data } = await supabase.storage
        .from('thumbnails')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });
        
      if (error) {
        console.error('Erro no upload de thumbnail:', error);
        return { error };
      }
      
      const { data: publicUrlData } = supabase.storage
        .from('thumbnails')
        .getPublicUrl(filePath);
        
      if (!publicUrlData || !publicUrlData.publicUrl) {
        return { 
          error: new Error('Falha ao obter URL pública da thumbnail')
        };
      }
      
      return { data: { publicUrl: publicUrlData.publicUrl } };
    } catch (err) {
      console.error('Erro inesperado no upload de thumbnail:', err);
      return { error: err instanceof Error ? err : new Error('Erro desconhecido no upload de thumbnail') };
    }
  }
};

// Serviço de bibliotecas
export const libraryService = {
  getLibraries: async () => {
    return await supabase
      .from('bibliotecas')
      .select('*');
  },
  
  getLibraryById: async (id: string) => {
    return await supabase
      .from('bibliotecas')
      .select('*')
      .eq('id', id)
      .single();
  },
  
  createLibrary: async (libraryData: any) => {
    return await supabase
      .from('bibliotecas')
      .insert(libraryData)
      .select();
  },
  
  updateLibrary: async (id: string, libraryData: any) => {
    return await supabase
      .from('bibliotecas')
      .update(libraryData)
      .eq('id', id);
  },
  
  deleteLibrary: async (id: string) => {
    return await supabase
      .from('bibliotecas')
      .delete()
      .eq('id', id);
  },
  
  // Obter materiais de uma biblioteca específica
  getLibraryMaterials: async (libraryId: string) => {
    return await supabase
      .from('biblioteca_materiais')
      .select('materiais(*)')
      .eq('biblioteca_id', libraryId);
  },
  
  // Adicionar um material a uma biblioteca
  addMaterialToLibrary: async (libraryId: string, materialId: string) => {
    return await supabase
      .from('biblioteca_materiais')
      .insert({ biblioteca_id: libraryId, material_id: materialId });
  },
  
  // Remover um material de uma biblioteca
  removeMaterialFromLibrary: async (libraryId: string, materialId: string) => {
    return await supabase
      .from('biblioteca_materiais')
      .delete()
      .eq('biblioteca_id', libraryId)
      .eq('material_id', materialId);
  },

  // Obter usuários com acesso a uma biblioteca privada
  getUsersWithAccess: async (libraryId: string) => {
    return await supabase
      .from('biblioteca_permissoes')
      .select('users(*)')
      .eq('biblioteca_id', libraryId);
  },

  // Adicionar permissão para um usuário acessar uma biblioteca
  grantLibraryAccess: async (libraryId: string, userId: string) => {
    return await supabase
      .from('biblioteca_permissoes')
      .insert({ biblioteca_id: libraryId, user_id: userId });
  },

  // Remover permissão de um usuário
  revokeLibraryAccess: async (libraryId: string, userId: string) => {
    return await supabase
      .from('biblioteca_permissoes')
      .delete()
      .eq('biblioteca_id', libraryId)
      .eq('user_id', userId);
  },

  // Verificar se um usuário tem acesso a uma biblioteca
  checkLibraryAccess: async (libraryId: string, userId: string) => {
    const { data, error } = await supabase
      .from('biblioteca_permissoes')
      .select('*')
      .eq('biblioteca_id', libraryId)
      .eq('user_id', userId)
      .single();
    
    return { hasAccess: !!data, error };
  },

  // Obter bibliotecas privadas às quais um usuário tem acesso
  getUserPrivateLibraries: async (userId: string) => {
    return await supabase
      .from('biblioteca_permissoes')
      .select('bibliotecas(*)')
      .eq('user_id', userId);
  }
};

// Adicionar esta função de utilidade para verificar se uma coluna existe
export const checkColumnExists = async (tableName: string, columnName: string): Promise<boolean> => {
  try {
    // Tenta buscar uma linha da tabela
    const { data, error } = await supabase
      .from(tableName)
      .select('*')
      .limit(1);
      
    if (error) {
      console.error(`Erro ao verificar coluna ${columnName} na tabela ${tableName}:`, error);
      return false;
    }
    
    // Verifica se a coluna existe no resultado
    return (data && data.length > 0 && columnName in data[0]) || false;
  } catch (err) {
    console.error(`Exceção ao verificar coluna ${columnName} na tabela ${tableName}:`, err);
    return false;
  }
};

// Função para carregar todos os materiais acessíveis a um usuário
export const getUserMaterials = async (userId: string): Promise<any> => {
  try {
    console.log('-------------- DIAGNÓSTICO COMPLETO --------------');
    console.log('Buscando materiais para o usuário:', userId);
    
    // Verificar se o usuário é admin
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();
      
    if (userError) {
      console.error('ERRO: Falha ao buscar dados do usuário:', userError);
      return { data: [], error: userError };
    }
    
    console.log('Dados do usuário:', userData);
    const isAdmin = userData?.tipo_usuario === 'admin';
    console.log('Usuário é admin?', isAdmin);
    
    // Se for admin, retornar todos os materiais
    if (isAdmin) {
      console.log('Usuário é admin - retornando todos os materiais');
      const { data: adminMaterials, error: matError } = await supabase
        .from('materiais')
        .select('*');
        
      if (matError) {
        console.error('Erro ao buscar materiais para admin:', matError);
        throw matError;
      }
      
      console.log(`Retornando ${adminMaterials?.length || 0} materiais para o admin`);
      return { data: adminMaterials || [], error: null };
    }
    
    console.log('Usuário comum - aplicando regras de permissão');
    
    // DEBUG: Verificar diretamente se existem materiais no sistema
    const { data: systemMaterials, error: matError } = await supabase
      .from('materiais')
      .select('*');
    
    console.log('DIAGNÓSTICO - Total de materiais no sistema:', systemMaterials?.length || 0);
    if (systemMaterials && systemMaterials.length > 0) {
      console.log('Exemplo de material:', systemMaterials[0]);
    }
    
    // DEBUG: Verificar a estrutura da tabela biblioteca_materiais
    const { data: sampleRelations, error: relError } = await supabase
      .from('biblioteca_materiais')
      .select('*')
      .limit(5);
      
    console.log('DIAGNÓSTICO - Amostra de relações biblioteca_materiais:', sampleRelations);
    console.log('Erro ao verificar relações?', relError);
    
    // 1. Buscar todas as bibliotecas
    const { data: allLibraries, error: libError } = await supabase
      .from('bibliotecas')
      .select('*');
      
    if (libError) {
      console.error('Erro ao buscar bibliotecas:', libError);
      throw libError;
    }
    
    console.log('DIAGNÓSTICO - Total de bibliotecas:', allLibraries?.length || 0);
    if (allLibraries && allLibraries.length > 0) {
      console.log('Exemplo de biblioteca:', allLibraries[0]);
    } else {
      console.log('ALERTA: Nenhuma biblioteca encontrada no sistema!');
      return { data: [], error: null };
    }
    
    // Verificar se is_public existe nas bibliotecas
    const hasIsPublic = allLibraries && allLibraries.length > 0 && 'is_public' in allLibraries[0];
    console.log('Bibliotecas têm o campo is_public?', hasIsPublic);
    
    if (!hasIsPublic) {
      console.log('ALERTA: Campo is_public não existe nas bibliotecas!');
      
      // SOLUÇÃO TEMPORÁRIA: Tratar todas as bibliotecas como públicas
      console.log('Tratando todas as bibliotecas como públicas para fins de teste');
      const publicLibraries = allLibraries || [];
      console.log(`Considerando ${publicLibraries.length} bibliotecas como públicas`);
      
      // Usar essas bibliotecas como acessíveis
      const accessibleLibraryIds = publicLibraries.map(lib => lib.id);
      
      // Verificar cada biblioteca manualmente
      let allAccessibleMaterials: any[] = [];
      
      for (const libId of accessibleLibraryIds) {
        console.log(`Tentando materiais da biblioteca ${libId} diretamente...`);
        
        // Verificar se existem materiais associados
        const { data: bibMaterials, error: bmError } = await supabase
          .from('biblioteca_materiais')
          .select('*')
          .eq('biblioteca_id', libId);
          
        console.log(`Relações para biblioteca ${libId}:`, bibMaterials?.length || 0);
        
        if (bmError) {
          console.error(`Erro ao buscar relações para biblioteca ${libId}:`, bmError);
          continue;
        }
        
        if (bibMaterials && bibMaterials.length > 0) {
          // Extrair IDs dos materiais
          const materialIds = bibMaterials.map(item => item.material_id);
          console.log(`IDs de materiais encontrados:`, materialIds);
          
          // Buscar detalhes dos materiais
          const { data: materials, error: detailsError } = await supabase
            .from('materiais')
            .select('*')
            .in('id', materialIds);
            
          if (detailsError) {
            console.error('Erro ao buscar detalhes dos materiais:', detailsError);
            continue;
          }
          
          console.log(`Materiais recuperados: ${materials?.length || 0}`);
          if (materials && materials.length > 0) {
            allAccessibleMaterials = [...allAccessibleMaterials, ...materials];
          }
        }
      }
      
      console.log(`Total de materiais acessíveis encontrados: ${allAccessibleMaterials.length}`);
      return { data: allAccessibleMaterials, error: null };
    }
    
    // Separar bibliotecas públicas
    const publicLibraries = allLibraries?.filter(lib => lib.is_public === true) || [];
    console.log(`Encontradas ${publicLibraries.length} bibliotecas públicas:`, 
      publicLibraries.map(lib => `${lib.nome} (${lib.id})`));
    
    // 2. Buscar bibliotecas privadas com permissão
    const { data: privateAccess, error: privError } = await supabase
      .from('biblioteca_permissoes')
      .select('biblioteca_id')
      .eq('user_id', userId);
      
    if (privError) {
      console.error('Erro ao buscar permissões de bibliotecas:', privError);
      throw privError;
    }
    
    console.log(`Encontradas ${privateAccess?.length || 0} permissões para bibliotecas privadas`);
    
    // 3. Combinar IDs de todas as bibliotecas acessíveis
    const accessibleLibraryIds = [
      ...publicLibraries.map(lib => lib.id),
      ...(privateAccess?.map(access => access.biblioteca_id) || [])
    ];
    
    console.log(`Total de ${accessibleLibraryIds.length} bibliotecas acessíveis:`, accessibleLibraryIds);
    
    if (accessibleLibraryIds.length === 0) {
      console.log('Nenhuma biblioteca acessível. Retornando lista vazia.');
      return { data: [], error: null };
    }
    
    // 4. Buscar materiais biblioteca por biblioteca
    let userMaterials: any[] = [];
    
    // DIAGNÓSTICO: Verificar se a tabela biblioteca_materiais tem os campos esperados
    // Primeiro, vamos fazer uma consulta genérica para entender a estrutura
    const { data: testRelation } = await supabase
      .from('biblioteca_materiais')
      .select('*')
      .limit(1);
      
    console.log('Estrutura da tabela biblioteca_materiais:', testRelation);
    
    for (const libId of accessibleLibraryIds) {
      console.log(`Buscando materiais da biblioteca ${libId}`);
      
      // Busca completa para diagnóstico
      const { data: fullBibMat, error: fullMatError } = await supabase
        .from('biblioteca_materiais')
        .select('*')
        .eq('biblioteca_id', libId);
        
      console.log(`Dados completos para biblioteca ${libId}:`, fullBibMat);
      
      if (fullMatError) {
        console.error(`Erro completo ao consultar biblioteca ${libId}:`, fullMatError);
      }
      
      // Verificar os nomes das colunas
      if (fullBibMat && fullBibMat.length > 0) {
        console.log('Colunas disponíveis:', Object.keys(fullBibMat[0]));
        
        // Determinar nome da coluna que contém ID do material
        const materialIdColumn = Object.keys(fullBibMat[0]).find(
          col => col === 'material_id' || col === 'materialId' || col.includes('material')
        );
        
        if (materialIdColumn) {
          console.log(`Coluna de ID de material encontrada: ${materialIdColumn}`);
          
          // Extrair IDs dos materiais usando o nome correto da coluna
          const materialIds = fullBibMat.map(item => item[materialIdColumn]);
          console.log('IDs de materiais:', materialIds);
          
          if (materialIds.length > 0) {
            // Buscar detalhes dos materiais
            const { data: materials, error: detailsError } = await supabase
              .from('materiais')
              .select('*')
              .in('id', materialIds);
              
            if (detailsError) {
              console.error('Erro ao buscar detalhes dos materiais:', detailsError);
              continue;
            }
            
            if (materials && materials.length > 0) {
              console.log(`Adicionando ${materials.length} materiais ao resultado`);
              userMaterials = [...userMaterials, ...materials];
            }
          }
          continue;
        }
      }
      
      // Se a abordagem anterior falhar, tente o método padrão
      const { data: bibMaterials, error: matSubError } = await supabase
        .from('biblioteca_materiais')
        .select('material_id')
        .eq('biblioteca_id', libId);
        
      if (matSubError) {
        console.error(`Erro ao buscar materiais da biblioteca ${libId}:`, matSubError);
        continue; // continua com a próxima biblioteca
      }
      
      if (bibMaterials && bibMaterials.length > 0) {
        console.log(`Encontrados ${bibMaterials.length} materiais na biblioteca ${libId}`);
        
        // Extrair IDs dos materiais
        const materialIds = bibMaterials.map(item => item.material_id);
        
        // Buscar detalhes dos materiais
        const { data: materials, error: detailsError } = await supabase
          .from('materiais')
          .select('*')
          .in('id', materialIds);
          
        if (detailsError) {
          console.error('Erro ao buscar detalhes dos materiais:', detailsError);
          continue;
        }
        
        if (materials && materials.length > 0) {
          console.log(`Adicionando ${materials.length} materiais ao resultado`);
          userMaterials = [...userMaterials, ...materials];
        }
      } else {
        console.log(`Nenhum material encontrado na biblioteca ${libId}`);
      }
    }
    
    // 5. Remover duplicatas
    const materialsMap = new Map();
    userMaterials.forEach(mat => {
      if (mat && mat.id) {
        materialsMap.set(mat.id, mat);
      }
    });
    
    const uniqueMaterials = Array.from(materialsMap.values());
    console.log(`Retornando ${uniqueMaterials.length} materiais únicos para o usuário comum`);
    
    if (uniqueMaterials.length === 0) {
      console.log('ALERTA FINAL: Nenhum material encontrado após todo o processamento!');
      
      // Verificar se existem materiais no sistema
      if (systemMaterials && systemMaterials.length > 0) {
        console.log('DIAGNÓSTICO FINAL: Existem materiais no sistema, mas o usuário não tem acesso a nenhum deles.');
      } else {
        console.log('DIAGNÓSTICO FINAL: Não há materiais cadastrados no sistema!');
      }
    }
    
    console.log('-------------- FIM DO DIAGNÓSTICO --------------');
    return { data: uniqueMaterials, error: null };
  } catch (error: any) {
    console.error('ERRO CRÍTICO ao carregar materiais do usuário:', error);
    return { data: [], error };
  }
}; 